import { useCallback, useContext, useEffect, useState } from 'react'

import { deliveryMethodHelpers, useFetch } from '@reveni-io/reveni-ui'

import RMAContext from '@/returns/pages/TransactionRMAPage/context/RMAContext'
import returnsServices from '@/returns/services/services'

import { formatDeliveryMethods } from './helpers'

const useDeliveryMethods = ({ setToastInfo, regionId, orderId }) => {
  const { rmaInfo, setRmaInfo } = useContext(RMAContext)

  const {
    data: deliveryMethods,
    loading: gettingDeliveryMethods,
    fetchData: getDeliveryMethods,
  } = useFetch(returnsServices.getRmaDeliveryMethods, { doInitialRequest: false })

  const { fetchData: addDeliveryMethodToRma, loading: addingDeliveryMethod } = useFetch(
    returnsServices.addDeliveryMethodToRma,
    {
      doInitialRequest: false,
    }
  )

  const [deliveryMethod, setDeliveryMethod] = useState(null)
  const { getAddDeliveryMethodPayload, getDeliveryMethodsConstraints } = deliveryMethodHelpers()

  const specialConstraints = getDeliveryMethodsConstraints({
    deliveryMethod,
    countryCode: rmaInfo?.orderInfo?.shipping_address?.country?.code,
    storeReturnAddressConfiguration: rmaInfo?.merchantInfo?.return_address_configuration,
  })

  const updateDeliveryMethods = useCallback(
    deliveryMethods => {
      setRmaInfo(rmaInfo => ({ ...rmaInfo, deliveryMethods: formatDeliveryMethods(deliveryMethods) }))
    },
    [setRmaInfo]
  )

  useEffect(() => {
    if (orderId && !deliveryMethods) {
      getDeliveryMethods({ orderId, regionId })
    }
  }, [orderId, regionId, deliveryMethods, getDeliveryMethods])

  useEffect(() => {
    if (deliveryMethods) {
      updateDeliveryMethods(deliveryMethods)
    }
  }, [deliveryMethods, updateDeliveryMethods])

  const handleGetDeliveryMethods = async ({ orderId, regionId, returnId }) => {
    try {
      const data = await getDeliveryMethods({ orderId, regionId, returnId })
      updateDeliveryMethods(data)
    } catch (err) {
      setToastInfo({ show: true, message: err?.errorMessage, type: 'error' })
    }
  }

  const onClickDeliveryMethod = id => {
    const deliveryMethod = rmaInfo.deliveryMethods.find(dm => dm.id === id)
    setDeliveryMethod(deliveryMethod)
    setRmaInfo(rmaInfo => ({
      ...rmaInfo,
      orderInfo: {
        ...rmaInfo.orderInfo,
        shipping_address: {
          ...rmaInfo.orderInfo.original_shipping_address,
          province: {
            ...rmaInfo.orderInfo.shipping_address.province,
          },
        },
        selectedPickupDate: null,
        selectedTimeRange: null,
        numberOfReturnLabels: 1,
        refundType: null,
      },
    }))
  }

  const onSelectTimeRange = id => {
    setRmaInfo(rmaInfo => {
      return {
        ...rmaInfo,
        orderInfo: { ...rmaInfo.orderInfo, selectedTimeRange: id },
      }
    })
  }

  const onSelectPickupDate = date => {
    setRmaInfo(rmaInfo => ({
      ...rmaInfo,
      orderInfo: { ...rmaInfo.orderInfo, selectedPickupDate: date },
    }))
  }

  const onSaveDeliveryMethod = async deliveryMethod => {
    try {
      const data = await addDeliveryMethodToRma({
        returnId: rmaInfo.orderInfo.returnId,
        orderId: rmaInfo.orderInfo.id,
        payload: getAddDeliveryMethodPayload({ ...rmaInfo.orderInfo, deliveryMethod }),
      })
      setRmaInfo(rmaInfo => ({
        ...rmaInfo,
        orderInfo: { ...rmaInfo.orderInfo, allowed_refund_methods: data?.allowed_refund_methods, deliveryMethod },
        orderAmount: data?.summary,
      }))
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const changeNumberOfReturnLabels = number => {
    setRmaInfo(rmaInfo => {
      return {
        ...rmaInfo,
        orderInfo: { ...rmaInfo.orderInfo, numberOfReturnLabels: number },
      }
    })
  }

  return {
    gettingDeliveryMethods,
    getDeliveryMethods: handleGetDeliveryMethods,
    deliveryMethods: rmaInfo.deliveryMethods,
    deliveryMethod,
    setDeliveryMethod,
    updateDeliveryMethods,
    onClickDeliveryMethod,
    onSelectTimeRange,
    onSelectPickupDate,
    onSaveDeliveryMethod,
    addingDeliveryMethod,
    specialConstraints,
    numberOfReturnLabels: rmaInfo.orderInfo.numberOfReturnLabels,
    maxNumberOfReturnLabels: rmaInfo?.items?.length,
    changeNumberOfReturnLabels,
  }
}

export default useDeliveryMethods
